import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { getUniqueFeaturesById, useGeoServerFeaturesList } from "@l2r-front/l2r-geodata"
import { HandIcon, PaintIcon, RubberIcon } from "@l2r-front/l2r-icons"
import {
    REFERENTIAL_MAP_ID,
    Draw,
    FREE_ROAM_MODE,
    PAINT_MODE,
    RUBBER_MODE,
    FreeRoamMode,
    StabiloMode,
    RubberMode,
} from "@l2r-front/l2r-map"
import { NetworkReferentialLayer, ReferentialMap, useNetworksStateContext, useSubNetworksWithWriteAccess } from "@l2r-front/l2r-networks"
import { UiModal, useIsMobileDevice, useTheme } from "@l2r-front/l2r-ui"

import { API_MODULE_ROADWORK } from "../../../../common/constants/appRoad"
import { useRoadwork } from "../../hooks/queries/useRoadwork"
import { parametricsToLinearLocations } from "../../utils/parametricsToLinearLocations"

import { RoadworkStabiloSectionLayer } from "../RoadworkStabiloSectionLayer"

import * as Styled from "./RoadworkSectionPainter.styled"

type RoadworkSectionPainterProps = {
    className?: string,
    cancelButtonLabel: string,
    desc: string,
    open: boolean,
    onClose: () => void,
    onSave: (_data: any) => void,
    saveButtonLabel: string,
    showLoader?: boolean,
    title: string,
}

export function RoadworkSectionPainter(props: RoadworkSectionPainterProps) {

    const {
        cancelButtonLabel,
        className,
        desc,
        onClose,
        open,
        onSave,
        showLoader = false,
        saveButtonLabel,
        title,
    } = props

    const [saveEnable, setSaveEnable] = useState(false)
    const [linearLocationsToSave, setLinearLocationsToSave] = useState([])
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()
    const { road, roadworkUuid } = useParams()
    const isMobile = useIsMobileDevice()
    const selectedNetworkWithWriteAccess = useSubNetworksWithWriteAccess(selectedSubNetworkSlugs, API_MODULE_ROADWORK)
    const [containerElement, setContainerElement] = useState(null)

    const queryParams = {
        layer: selectedNetwork["referential"].layerSection,
        linear_location_road: road,
        as_geojson: true,
    }
    const { data: roadFeatures } = useGeoServerFeaturesList(
        ["paintingRoad", queryParams],
        { ...queryParams },
        {
            enabled: !!road && !!selectedNetwork["referential"].layerSection,
        },
    )

    const { data: sectionSegments } = useRoadwork(roadworkUuid, null, {}, { "as_geojson": true })

    const filteredRoadFeatures = useMemo(() => {
        if (!roadFeatures) {
            return null
        }

        if (!selectedNetworkWithWriteAccess.length) {
            return roadFeatures
        }

        const features = roadFeatures.features.filter(roadFeature => {
            const inSelectedSubNetwork = roadFeature.sub_network_slugs.some(slug =>
                selectedNetworkWithWriteAccess.includes(slug),
            )

            const inSection = sectionSegments?.linear_location?.some(section =>
                section.start >= roadFeature.linear_location[0].start &&
                section.end <= roadFeature.linear_location[0].end,
            )

            return inSelectedSubNetwork || inSection || !selectedNetwork?.children?.length
        })

        const filtered = {
            ...roadFeatures,
            features: getUniqueFeaturesById(features),
        }
        return filtered
    }, [roadFeatures, sectionSegments, selectedNetwork, selectedNetworkWithWriteAccess])

    const handleFeaturesChanged = useCallback(featureCollection => {
        const linearLocations = parametricsToLinearLocations(
            filteredRoadFeatures,
            featureCollection.features,
        )
        const seen = new Set()
        const uniqueLinearLocations = linearLocations.filter(([start, end]) => {
            const key = `${start}-${end}`
            if (seen.has(key)) {
                return false
            }
            seen.add(key)
            return true
        })

        setSaveEnable(uniqueLinearLocations.length > 0)
        setLinearLocationsToSave(uniqueLinearLocations)
    }, [filteredRoadFeatures])

    const handleSave = useCallback(() => {
        if (linearLocationsToSave.length) {
            onSave({
                linearLocation: linearLocationsToSave.map(ll => ({
                    typeid: 1,
                    road: road,
                    start: ll[0],
                    end: ll[1],
                })),
            })
            return
        }
        onClose()
    }, [onClose, onSave, linearLocationsToSave, road])

    const buttons = useMemo(() => {
        return <>
            <Styled.Button id={"linear-edit-cancel"} isMobile={isMobile} variant="tertiary" onClick={onClose}>{cancelButtonLabel}</Styled.Button>
            <Styled.Button id={"linear-edit-ok"} isMobile={isMobile} disabled={!saveEnable} onClick={handleSave}>{saveButtonLabel}</Styled.Button>
        </>
    }, [cancelButtonLabel, handleSave, isMobile, onClose, saveEnable, saveButtonLabel])

    const theme = useTheme()

    const modesConfig = {
        [FREE_ROAM_MODE]: {
            mode: FreeRoamMode,
            icon: <HandIcon />,
            brushes: [{ name: "black", token: "common/black_states" }],
        },
        [PAINT_MODE]: {
            mode: StabiloMode,
            icon: <PaintIcon />,
            brushes: [
                { name: "primary", token: "primary" },
            ],
        },
        [RUBBER_MODE]: {
            mode: RubberMode,
            icon: <RubberIcon />,
            brushes: [{ name: "black", token: "common/black_states" }],
        },
    }

    if (!filteredRoadFeatures) {
        return null
    } else {
        return (
            <UiModal
                buttons={buttons}
                className={className}
                desc={desc}
                open={open}
                onClose={onClose}
                title={title}
                showLoader={showLoader}
            >
                {open && <ReferentialMap topLeftElements={[<Styled.DrawContainer ref={setContainerElement} />]}>
                    <NetworkReferentialLayer interactive={false} mapId={REFERENTIAL_MAP_ID} subNetworks={selectedNetworkWithWriteAccess} module={API_MODULE_ROADWORK} />
                    <RoadworkStabiloSectionLayer mapId={REFERENTIAL_MAP_ID} />
                    <Draw
                        container={containerElement}
                        defaultMode={PAINT_MODE}
                        modesConfig={modesConfig}
                        lineColorStyle={theme.palette["brand logiroad/primary/300"].main}
                        opts={{ roadSegments: filteredRoadFeatures, startingFeatures: sectionSegments }}
                        onFeaturesChange={handleFeaturesChanged} />
                </ReferentialMap>}
            </UiModal>
        )
    }
}